import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Shield, TrendingUp, BarChart3, CheckCircle, Users, DollarSign, FileText, Target, Award, Phone, Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const LandingPage = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="px-6 py-6 bg-white border-b border-slate-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center justify-center">
            <img
              src="https://customer-assets.emergentagent.com/job_fintech-portal-38/artifacts/7o5rddiv_veriquant_logo%20%281%29.webp"
              alt="Veriquant"
              className="h-[64px] w-auto cursor-pointer"
              style={{ imageRendering: 'crisp-edges', WebkitFontSmoothing: 'antialiased' }}
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            />
          </div>
          
          {/* Desktop Navigation Menu */}
          <nav className="hidden lg:flex items-center space-x-2">
            <a href="#about" className="px-3 py-1.5 text-sm font-medium text-slate-700 hover:text-white hover:bg-blue-600 bg-slate-100 rounded-md transition-all">About</a>
            <a href="#how-it-works" className="px-3 py-1.5 text-sm font-medium text-slate-700 hover:text-white hover:bg-blue-600 bg-slate-100 rounded-md transition-all whitespace-nowrap">How It Works</a>
            <a href="#investment-strategy" className="px-3 py-1.5 text-sm font-medium text-slate-700 hover:text-white hover:bg-blue-600 bg-slate-100 rounded-md transition-all">Strategy</a>
            <a href="#performance" className="px-3 py-1.5 text-sm font-medium text-slate-700 hover:text-white hover:bg-blue-600 bg-slate-100 rounded-md transition-all">Performance</a>
            <a href="#team" className="px-3 py-1.5 text-sm font-medium text-slate-700 hover:text-white hover:bg-blue-600 bg-slate-100 rounded-md transition-all">Team</a>
            <a href="#faq" className="px-3 py-1.5 text-sm font-medium text-slate-700 hover:text-white hover:bg-blue-600 bg-slate-100 rounded-md transition-all">FAQ</a>
            <a href="#contact" className="px-3 py-1.5 text-sm font-medium text-slate-700 hover:text-white hover:bg-blue-600 bg-slate-100 rounded-md transition-all">Contact</a>
          </nav>

          <div className="flex items-center space-x-4">
            {/* Social Media Links */}
            <div className="hidden md:flex items-center space-x-3 mr-2">
              <a 
                href="https://www.instagram.com/veriquant/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-slate-600 hover:text-blue-600 transition-colors"
                aria-label="Instagram"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a 
                href="https://www.facebook.com/profile.php?id=61578391771762" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-slate-600 hover:text-blue-600 transition-colors"
                aria-label="Facebook"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a 
                href="https://www.linkedin.com/company/veriquant-asset-management/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-slate-600 hover:text-blue-600 transition-colors"
                aria-label="LinkedIn"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </div>
            
            <Button
              variant="outline"
              onClick={() => navigate('/admin/login')}
              className="border-slate-300 hover:bg-slate-50"
            >
              <Shield className="w-4 h-4 mr-2" />
              Admin
            </Button>
            <Button
              onClick={() => navigate('/login')}
              className="bg-blue-600 hover:bg-blue-700 text-white hidden sm:flex"
            >
              Investor Portal
            </Button>
            
            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-slate-200 shadow-lg">
            <nav className="flex flex-col p-4 space-y-2">
              <a 
                href="#about" 
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 text-sm font-semibold text-slate-700 hover:text-white hover:bg-blue-600 bg-slate-100 rounded-lg transition-all"
              >
                About
              </a>
              <a 
                href="#how-it-works" 
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 text-sm font-semibold text-slate-700 hover:text-white hover:bg-blue-600 bg-slate-100 rounded-lg transition-all"
              >
                How It Works
              </a>
              <a 
                href="#investment-strategy" 
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 text-sm font-semibold text-slate-700 hover:text-white hover:bg-blue-600 bg-slate-100 rounded-lg transition-all"
              >
                Strategy
              </a>
              <a 
                href="#performance" 
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 text-sm font-semibold text-slate-700 hover:text-white hover:bg-blue-600 bg-slate-100 rounded-lg transition-all"
              >
                Performance
              </a>
              <a 
                href="#team" 
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 text-sm font-semibold text-slate-700 hover:text-white hover:bg-blue-600 bg-slate-100 rounded-lg transition-all"
              >
                Team
              </a>
              <a 
                href="#faq" 
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 text-sm font-semibold text-slate-700 hover:text-white hover:bg-blue-600 bg-slate-100 rounded-lg transition-all"
              >
                FAQ
              </a>
              <a 
                href="#contact" 
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 text-sm font-semibold text-slate-700 hover:text-white hover:bg-blue-600 bg-slate-100 rounded-lg transition-all"
              >
                Contact
              </a>
              <div className="pt-2 border-t border-slate-200 space-y-2">
                <Button
                  onClick={() => { setMobileMenuOpen(false); navigate('/admin/login'); }}
                  variant="outline"
                  className="w-full border-slate-300 hover:bg-slate-50"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Admin
                </Button>
                <Button
                  onClick={() => { setMobileMenuOpen(false); navigate('/login'); }}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Investor Portal
                </Button>
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-slate-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div>
              <div className="inline-block bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                🚀 Now Accepting New Investors
              </div>
              
              {/* VARIATION 6: Tagline in Hero (Above Main Heading) - More Prominent */}
              {/* <p className="text-lg text-blue-600 font-semibold mb-3 tracking-wide">
                Simple • Fast • Flexible Investing
              </p> */}
              
              <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
                Access the World of{' '}
                <span className="text-blue-600">Alternative Investments</span>
              </h1>
              
              {/* VARIATION 7: Tagline in Hero (Below Main Heading) - Subtitle Style */}
              {/* <p className="text-xl text-slate-700 font-medium mb-6 border-l-4 border-blue-600 pl-4">
                Merchant Cash Advances: Simple. Fast. Flexible.
              </p> */}
              <p className="text-xl text-slate-600 mb-4 leading-relaxed">
                Veriquant provides access to investment opportunities within the Small Business Finance asset class through our carefully vetted partner platforms.
              </p>
              <p className="text-lg text-slate-600 mb-6">
                Join sophisticated investors who are diversifying their portfolios with high-yield MCA investments. Through our partner platforms, you gain access to institutional-grade due diligence, real-time performance tracking, and flexible investment terms. <span className="font-semibold text-slate-900">Minimum investment: $250,000.</span>
              </p>
              
              {/* Quick Benefits */}
              <div className="bg-white rounded-xl p-6 shadow-md mb-6 border border-blue-100">
                <h3 className="font-bold text-slate-900 mb-3">Why Choose Veriquant?</h3>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0" />
                    <span className="text-slate-700">20-30% annual return on direct MCA investments</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0" />
                    <span className="text-slate-700">Direct ownership - you control your investments</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0" />
                    <span className="text-slate-700">Reinvest capital to compound your returns</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0" />
                    <span className="text-slate-700">Transparent, real-time portfolio tracking</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  onClick={() => {
                    const contactSection = document.getElementById('contact');
                    contactSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg"
                >
                  Start Investing Today
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => window.location.href = 'tel:6177759876'}
                  className="border-slate-300 hover:bg-slate-50 px-8 py-6 text-lg"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Call: 617.775.9876
                </Button>
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-gradient-to-br from-blue-50 to-white p-8">
                <img 
                  src="https://customer-assets.emergentagent.com/job_import-wizard-19/artifacts/z8hfwugu_image.png" 
                  alt="Isometric Business Growth and Success Illustration"
                  className="w-full h-[500px] object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section id="about" className="py-12 bg-slate-900 text-white scroll-mt-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="flex items-center justify-center mb-4">
                <Users className="w-12 h-12 text-blue-400" />
              </div>
              <div className="text-5xl font-bold mb-2">45K+</div>
              <div className="text-slate-300 text-lg">Investments</div>
            </div>
            <div>
              <div className="flex items-center justify-center mb-4">
                <DollarSign className="w-12 h-12 text-green-400" />
              </div>
              <div className="text-5xl font-bold mb-2">$2B+</div>
              <div className="text-slate-300 text-lg">Total Money Dispersed</div>
            </div>
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-6 border-2 border-blue-400">
              <div className="flex items-center justify-center mb-4">
                <Award className="w-12 h-12 text-blue-200" />
              </div>
              <div className="text-5xl font-bold mb-2">20-30%</div>
              <div className="text-blue-100 text-lg font-semibold">Annual Return</div>
              <div className="text-xs text-blue-200 mt-2">Direct Investment</div>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Insights & Market Data */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Industry Insights & Market Data</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Access to real-time data and insights from leading MCA platforms
            </p>
          </div>

          {/* Market Statistics Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 text-center border border-blue-200">
              <div className="text-3xl font-bold text-blue-600 mb-2">$25B</div>
              <div className="text-sm text-slate-700 font-medium">Annual MCA Market Size</div>
              <div className="text-xs text-slate-500 mt-1">2025 Industry Estimated Amount</div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 text-center border border-green-200">
              <div className="text-3xl font-bold text-green-600 mb-2">350K+</div>
              <div className="text-sm text-slate-700 font-medium">Small Businesses Funded</div>
              <div className="text-xs text-slate-500 mt-1">Nationwide in 2024</div>
            </div>
            <div className="bg-gradient-to-br from-violet-50 to-violet-100 rounded-xl p-6 text-center border border-violet-200">
              <div className="text-3xl font-bold text-violet-600 mb-2">20-30%</div>
              <div className="text-sm text-slate-700 font-medium">Average Annual Return</div>
              <div className="text-xs text-slate-500 mt-1">Industry Average</div>
            </div>
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 text-center border border-orange-200">
              <div className="text-3xl font-bold text-orange-600 mb-2">6-12 mo</div>
              <div className="text-sm text-slate-700 font-medium">Average Deal Duration</div>
              <div className="text-xs text-slate-500 mt-1">Typical Term Length</div>
            </div>
          </div>

          {/* Industry Breakdown */}
          <div className="bg-slate-50 rounded-2xl p-8 mb-12">
            <h3 className="text-2xl font-bold text-slate-900 mb-6 text-center">Top Industries Funded</h3>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-slate-900">Restaurant & Food</span>
                  <span className="text-blue-600 font-bold">23%</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div className="bg-blue-600 h-2 rounded-full" style={{ width: '23%' }}></div>
                </div>
              </div>
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-slate-900">Retail & E-commerce</span>
                  <span className="text-green-600 font-bold">18%</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div className="bg-green-600 h-2 rounded-full" style={{ width: '18%' }}></div>
                </div>
              </div>
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-slate-900">Healthcare Services</span>
                  <span className="text-violet-600 font-bold">15%</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div className="bg-violet-600 h-2 rounded-full" style={{ width: '15%' }}></div>
                </div>
              </div>
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-slate-900">Construction</span>
                  <span className="text-orange-600 font-bold">12%</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div className="bg-orange-600 h-2 rounded-full" style={{ width: '12%' }}></div>
                </div>
              </div>
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-slate-900">Professional Services</span>
                  <span className="text-pink-600 font-bold">11%</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div className="bg-pink-600 h-2 rounded-full" style={{ width: '11%' }}></div>
                </div>
              </div>
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-slate-900">Auto Services</span>
                  <span className="text-teal-600 font-bold">9%</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div className="bg-teal-600 h-2 rounded-full" style={{ width: '9%' }}></div>
                </div>
              </div>
            </div>
          </div>

          {/* Key Metrics Row */}
          {/* Stats boxes removed */}
        </div>
      </section>

      {/* Our Process */}
      <section id="how-it-works" className="py-12 bg-slate-50 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">How It Works</h2>
            <p className="text-xl text-slate-600">Discover how it all happens</p>
            <p className="text-lg text-slate-600 mt-2">
              From start to finish, learn all the intricate steps of the lifecycle of an investment on our platform.
            </p>
          </div>

          {/* Process Steps */}
          <div className="space-y-16">
            {/* Origination */}
            <div className="bg-white rounded-2xl shadow-lg p-8 lg:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="flex items-center mb-4">
                    <FileText className="w-10 h-10 text-blue-600 mr-4" />
                    <h3 className="text-3xl font-bold text-slate-900">Origination</h3>
                  </div>
                  <p className="text-xl font-semibold text-blue-600 mb-4">
                    Partner platform deal offerings undergo comprehensive underwriting and review
                  </p>
                  <p className="text-slate-600 leading-relaxed mb-4">
                    Veriquant curates access to leading originators and their platforms after performing detailed due diligence. We take a methodical and systematic approach to evaluate and select platform partners.
                  </p>
                  <p className="text-slate-600 leading-relaxed">
                    We're constantly seeking partnerships with platforms that can deliver quality opportunities aligned with our investors' long-term goals.
                  </p>
                </div>
                <div className="relative rounded-xl overflow-hidden h-64 shadow-lg bg-gradient-to-br from-blue-50 to-white p-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_import-wizard-19/artifacts/ehjt899q_eb6a5fe6-26ed-471a-b63e-17910004d557.avif" 
                    alt="Investment Decision Analysis"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
            </div>

            {/* Due Diligence */}
            <div className="bg-white rounded-2xl shadow-lg p-8 lg:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div className="order-2 lg:order-1 relative rounded-xl overflow-hidden h-64 shadow-lg bg-gradient-to-br from-blue-50 to-white p-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_import-wizard-19/artifacts/rpk5nwoo_istockphoto-1358825143-612x612.jpg" 
                    alt="Due Diligence and Analysis"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="order-1 lg:order-2">
                  <div className="flex items-center mb-4">
                    <Shield className="w-10 h-10 text-green-600 mr-4" />
                    <h3 className="text-3xl font-bold text-slate-900">Due Diligence</h3>
                  </div>
                  <p className="text-xl font-semibold text-green-600 mb-4">
                    Veriquant's due diligence review
                  </p>
                  <p className="text-slate-600 leading-relaxed mb-4">
                    Our investment team conducts extensive review and analysis of each platform partner and their investment opportunities. The process typically takes several months before a potential partner is presented to our investment committee for approval.
                  </p>
                  <p className="text-slate-600 leading-relaxed mb-4">
                    If a platform partner is recommended by the due diligence team, the next step includes a presentation to our investment committee for final evaluation.
                  </p>
                  <p className="text-slate-600 leading-relaxed">
                    The experienced investment committee reviews each potential partnership, challenges the due diligence team's thesis, and weighs potential risk and reward for Veriquant investors accessing these opportunities.
                  </p>
                </div>
              </div>
            </div>

            {/* Investment */}
            <div className="bg-white rounded-2xl shadow-lg p-8 lg:p-12">
              <div className="text-center mb-8">
                <div className="flex items-center justify-center mb-4">
                  <TrendingUp className="w-10 h-10 text-violet-600 mr-4" />
                  <h3 className="text-3xl font-bold text-slate-900">Investment</h3>
                </div>
                <p className="text-xl font-semibold text-violet-600 mb-4">
                  Investors: Balancing Risk vs. Reward
                </p>
              </div>
              <div className="max-w-4xl mx-auto">
                <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-lg mb-6">
                  <p className="text-slate-700 leading-relaxed">
                    At Veriquant, we provide access to investment opportunities designed to serve as complementary, diversifying investments for traditional 60/40 portfolios. Through our partner platforms, investors can access a range of terms from 1 to 3 years, with flexible distribution options — monthly, quarterly, or end-of-term payouts — providing flexibility to tailor investments to their time horizon and preferred payment structure.
                  </p>
                </div>
                <p className="text-slate-600 text-center">
                  For more information, call our investment team at <a href="tel:6177759876" className="text-blue-600 font-semibold hover:underline">617.775.9876</a>.
                </p>
              </div>
            </div>

            {/* Performance */}
            <div className="bg-white rounded-2xl shadow-lg p-8 lg:p-12">
              <div className="text-center mb-8">
                <div className="flex items-center justify-center mb-4">
                  <BarChart3 className="w-10 h-10 text-blue-600 mr-4" />
                  <h3 className="text-3xl font-bold text-slate-900">Performance</h3>
                </div>
                <p className="text-xl font-semibold text-blue-600">
                  Reporting made simple to evaluate your portfolio
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 text-center">
                  <BarChart3 className="w-12 h-12 text-blue-600 mx-auto mb-3" />
                  <h4 className="font-semibold text-slate-900 mb-2">Real-Time Analytics</h4>
                  <p className="text-sm text-slate-600">Access live data from partner platforms</p>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 text-center">
                  <FileText className="w-12 h-12 text-green-600 mx-auto mb-3" />
                  <h4 className="font-semibold text-slate-900 mb-2">Detailed Reports</h4>
                  <p className="text-sm text-slate-600">Comprehensive performance aggregation</p>
                </div>
                <div className="bg-gradient-to-br from-violet-50 to-violet-100 rounded-xl p-6 text-center">
                  <TrendingUp className="w-12 h-12 text-violet-600 mx-auto mb-3" />
                  <h4 className="font-semibold text-slate-900 mb-2">Portfolio Insights</h4>
                  <p className="text-sm text-slate-600">Curated data for informed decisions</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Returns Section */}
      <section id="performance" className="py-12 bg-white scroll-mt-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Returns</h2>
            <p className="text-2xl font-semibold text-blue-600 mb-8">Get more returns on your returns</p>
          </div>

          {/* Performance Benchmarks */}
          <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl p-8 mb-12">
            <h3 className="text-2xl font-bold text-slate-900 mb-6 text-center">Historical Performance vs Traditional Assets</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white rounded-xl p-6 text-center shadow-md border-2 border-blue-500">
                <div className="text-lg font-semibold text-slate-900 mb-2">Direct MCA Investments</div>
                <div className="text-4xl font-bold text-blue-600 mb-1">25-30%</div>
                <div className="text-xs text-slate-500">Annual Return</div>
              </div>
              <div className="bg-white rounded-xl p-6 text-center shadow-md">
                <div className="text-lg font-semibold text-slate-900 mb-2">S&P 500</div>
                <div className="text-4xl font-bold text-slate-600 mb-1">10.5%</div>
                <div className="text-xs text-slate-500">10-Year Average</div>
              </div>
              <div className="bg-white rounded-xl p-6 text-center shadow-md">
                <div className="text-lg font-semibold text-slate-900 mb-2">Corporate Bonds</div>
                <div className="text-4xl font-bold text-slate-600 mb-1">4.2%</div>
                <div className="text-xs text-slate-500">Investment Grade</div>
              </div>
              <div className="bg-white rounded-xl p-6 text-center shadow-md">
                <div className="text-lg font-semibold text-slate-900 mb-2">Savings Account</div>
                <div className="text-4xl font-bold text-slate-600 mb-1">0.5%</div>
                <div className="text-xs text-slate-500">National Average</div>
              </div>
            </div>
          </div>

          {/* Risk Management Metrics */}
          <div className="bg-slate-900 text-white rounded-2xl p-8 mb-12">
            <h3 className="text-2xl font-bold mb-6 text-center">Risk Management & Portfolio Health</h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-green-400 mb-2">&lt; 6%</div>
                <div className="text-sm text-slate-300">Default Rate</div>
                <div className="text-xs text-slate-400 mt-1">Industry Leading</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-400 mb-2">720+</div>
                <div className="text-sm text-slate-300">Avg FICO Score</div>
                <div className="text-xs text-slate-400 mt-1">Portfolio Average</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-violet-400 mb-2">15+</div>
                <div className="text-sm text-slate-300">Industries</div>
                <div className="text-xs text-slate-400 mt-1">Diversification</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-orange-400 mb-2">1.35x</div>
                <div className="text-sm text-slate-300">Avg Factor Rate</div>
                <div className="text-xs text-slate-400 mt-1">Return Multiple</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto items-center">
            <div className="bg-blue-50 border-l-4 border-blue-600 p-8 rounded-lg">
              <Award className="w-12 h-12 text-blue-600 mb-4" />
              <p className="text-slate-700 leading-relaxed">
                Veriquant was founded by a team of small business financing experts and investment managers who recognized an opportunity to provide sophisticated investors with curated access to high-yield MCA investments through carefully vetted partner platforms, streamlining the path to superior returns.
              </p>
            </div>
            
            <div className="relative rounded-xl overflow-hidden shadow-2xl h-64 bg-gradient-to-br from-blue-600 to-blue-700">
              <img 
                src="https://images.unsplash.com/photo-1652449823136-b279fbe5dfd3?w=500&h=400&fit=crop" 
                alt="3D Geometric Growth Pattern"
                className="w-full h-full object-cover opacity-20"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center text-white px-6">
                  <div className="text-5xl font-bold mb-2">20-30%</div>
                  <div className="text-xl">Annual Return</div>
                  <div className="text-sm mt-2 text-blue-100">on MCA Investments</div>
                </div>
              </div>
            </div>
            
            <div className="bg-green-50 border-l-4 border-green-600 p-8 rounded-lg">
              <CheckCircle className="w-12 h-12 text-green-600 mb-4" />
              <p className="text-slate-700 leading-relaxed font-semibold">
                Access direct MCA investment opportunities through our partner platforms with full transparency. Reinvest your capital to compound returns or withdraw funds as deals complete. You maintain complete control over your investment strategy.
              </p>
            </div>
          </div>

          {/* Payment Frequency Distribution */}
          <div className="mt-12 bg-slate-50 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-slate-900 mb-6 text-center">Payment Frequency Distribution*</h3>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="bg-white rounded-lg p-4 text-center border border-slate-200">
                <div className="text-2xl font-bold text-blue-600 mb-1">65%</div>
                <div className="text-sm text-slate-600">Daily</div>
              </div>
              <div className="bg-white rounded-lg p-4 text-center border border-slate-200">
                <div className="text-2xl font-bold text-green-600 mb-1">20%</div>
                <div className="text-sm text-slate-600">Weekly</div>
              </div>
              <div className="bg-white rounded-lg p-4 text-center border border-slate-200">
                <div className="text-2xl font-bold text-violet-600 mb-1">10%</div>
                <div className="text-sm text-slate-600">Bi-Weekly</div>
              </div>
              <div className="bg-white rounded-lg p-4 text-center border border-slate-200">
                <div className="text-2xl font-bold text-orange-600 mb-1">4%</div>
                <div className="text-sm text-slate-600">Monthly</div>
              </div>
              <div className="bg-white rounded-lg p-4 text-center border border-slate-200">
                <div className="text-2xl font-bold text-pink-600 mb-1">1%</div>
                <div className="text-sm text-slate-600">Other</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fund Section Removed */}
      <section className="py-12 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900" style={{display: 'none'}}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-block bg-blue-500 text-white px-6 py-2 rounded-full text-sm font-bold mb-4">
              INSTITUTIONAL OPPORTUNITY
            </div>
            <h2 className="text-5xl font-bold text-white mb-6">$100 Million Approved Fund</h2>
            <p className="text-2xl text-blue-100 mb-4">Direct MCA Investment Fund for Qualified Investors</p>
            <p className="text-xl text-slate-300 max-w-4xl mx-auto">
              Veriquant provides access to a $100M approved fund specifically dedicated to direct MCA investments through our partner platforms, delivering institutional-grade opportunities with enhanced due diligence and portfolio oversight.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
                <Shield className="w-8 h-8 mr-3 text-blue-400" />
                Fund Structure & Benefits
              </h3>
              <ul className="space-y-4 text-slate-200">
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-green-400 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <span className="font-semibold text-white">Minimum Investment: $250,000</span>
                    <p className="text-sm text-slate-300">Entry point for accredited investors</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-green-400 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <span className="font-semibold text-white">Maximum Allocation: $10,000,000</span>
                    <p className="text-sm text-slate-300">Institutional-sized opportunities available</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-green-400 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <span className="font-semibold text-white">Direct Ownership Model</span>
                    <p className="text-sm text-slate-300">You own the MCAs directly, not a pooled share</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-green-400 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <span className="font-semibold text-white">Full Transparency</span>
                    <p className="text-sm text-slate-300">Real-time tracking of every investment</p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
                <Target className="w-8 h-8 mr-3 text-green-400" />
                Expected Returns & Performance
              </h3>
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-green-500/20 to-green-600/20 rounded-xl p-6 border border-green-400/30">
                  <div className="text-5xl font-bold text-green-400 mb-2">25-30%</div>
                  <div className="text-white font-semibold mb-2">Annual Returns</div>
                  <p className="text-sm text-slate-300">Direct MCA investments with daily/weekly repayments</p>
                </div>
                <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 rounded-xl p-6 border border-blue-400/30">
                  <div className="text-4xl font-bold text-blue-400 mb-2">6-12 Months</div>
                  <div className="text-white font-semibold mb-2">Typical Duration</div>
                  <p className="text-sm text-slate-300">Capital returned for reinvestment or withdrawal</p>
                </div>
                <div className="bg-gradient-to-br from-violet-500/20 to-violet-600/20 rounded-xl p-6 border border-violet-400/30">
                  <div className="text-4xl font-bold text-violet-400 mb-2">&lt; 3%</div>
                  <div className="text-white font-semibold mb-2">Default Rate</div>
                  <p className="text-sm text-slate-300">Industry-leading risk management</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-10 text-center border-2 border-blue-400">
            <h3 className="text-3xl font-bold text-white mb-4">Qualified Investors Only</h3>
            <p className="text-xl text-blue-100 mb-6 max-w-3xl mx-auto">
              This fund is available exclusively to accredited investors, high-net-worth individuals, family offices, and institutional investors who meet SEC qualification requirements.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                onClick={() => window.location.href = 'tel:6177759876'}
                className="bg-white text-blue-600 hover:bg-slate-100 px-10 py-6 text-lg font-bold"
              >
                <Phone className="w-6 h-6 mr-2" />
                Schedule Fund Consultation
              </Button>
              <Button
                size="lg"
                onClick={() => navigate('/login?mode=register')}
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 px-10 py-6 text-lg font-bold"
              >
                Apply Now
                <ArrowRight className="w-6 h-6 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {/* Testimonials section removed */}

      {/* Investment Strategy */}
      <section id="investment-strategy" className="py-12 bg-white scroll-mt-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">How Direct MCA Investing Works</h2>
            <p className="text-xl text-slate-600">Build wealth through strategic MCA investments with full control and transparency</p>
          </div>

          {/* Featured Image Banner */}
          <div className="relative rounded-2xl overflow-hidden shadow-2xl mb-12 h-64 bg-gradient-to-r from-slate-900 to-blue-900">
            <img 
              src="https://images.unsplash.com/photo-1617518010245-0fa70dbd55ad?w=1200&h=400&fit=crop" 
              alt="Modern 3D Geometric Technology"
              className="w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-white px-6">
                <div className="text-5xl font-bold mb-3">Direct MCA Investments</div>
                <div className="text-2xl font-semibold">Institutional-Grade Opportunities</div>
                <div className="text-lg mt-2 text-blue-100">$250K - $10M Investment Range</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200 rounded-2xl p-8">
              <div className="bg-blue-600 text-white w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto text-3xl">
                💰
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3 text-center">Direct Investment</h3>
              <div className="text-center mb-6">
                <div className="text-4xl font-bold text-blue-600 mb-2">25-30%</div>
                <div className="text-slate-600">Annual Returns</div>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-1 flex-shrink-0" />
                  <span className="text-slate-700">Own MCAs directly through partner platforms</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-1 flex-shrink-0" />
                  <span className="text-slate-700">Full visibility into every business you're funding</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-1 flex-shrink-0" />
                  <span className="text-slate-700">Daily or weekly repayments from businesses</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-1 flex-shrink-0" />
                  <span className="text-slate-700">6-12 month typical deal duration</span>
                </li>
              </ul>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-white border-2 border-green-200 rounded-2xl p-8 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-green-600 text-white px-4 py-1 rounded-full text-sm font-bold">
                RECOMMENDED
              </div>
              <div className="bg-green-600 text-white w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto text-3xl">
                🔄
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3 text-center">Capital Reinvestment</h3>
              <div className="text-center mb-6">
                <div className="text-4xl font-bold text-green-600 mb-2">Compound</div>
                <div className="text-slate-600">Your Wealth</div>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-1 flex-shrink-0" />
                  <span className="text-slate-700">Automatically reinvest returns into new deals</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-1 flex-shrink-0" />
                  <span className="text-slate-700">Compound your returns year over year</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-1 flex-shrink-0" />
                  <span className="text-slate-700">Build a diversified MCA portfolio quickly</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-1 flex-shrink-0" />
                  <span className="text-slate-700">Or withdraw funds as deals complete</span>
                </li>
              </ul>
            </div>
            <div className="bg-gradient-to-br from-violet-50 to-white border-2 border-violet-200 rounded-2xl p-8">
              <div className="bg-violet-600 text-white w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto text-3xl">
                📊
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3 text-center">Full Control</h3>
              <div className="text-center mb-6">
                <div className="text-4xl font-bold text-violet-600 mb-2">100%</div>
                <div className="text-slate-600">Transparency</div>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-1 flex-shrink-0" />
                  <span className="text-slate-700">Track each investment in real-time via our portal</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-1 flex-shrink-0" />
                  <span className="text-slate-700">Access daily payment flows and business health data</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-1 flex-shrink-0" />
                  <span className="text-slate-700">Choose which vetted deals to invest in</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-1 flex-shrink-0" />
                  <span className="text-slate-700">Comprehensive portfolio analytics in one place</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Investor Control */}
      <section className="py-12 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Investor Control</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-6">
              Where traditional investment firms typically build black boxes around their investments, we curate access to transparent partner platforms that deliver a compelling and clear investing experience.
            </p>
          </div>
          <div className="bg-white rounded-2xl shadow-lg p-12 max-w-4xl mx-auto text-center">
            <Shield className="w-16 h-16 text-blue-600 mx-auto mb-6" />
            <h3 className="text-2xl font-bold text-slate-900 mb-4">
              Looking for your next investment opportunity?
            </h3>
            <p className="text-xl text-blue-600 font-semibold mb-8">Consider Veriquant</p>
            <Button
              size="lg"
              onClick={() => {
                const contactSection = document.getElementById('contact');
                contactSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-6 text-lg"
            >
              Get Started Today
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-12 bg-white scroll-mt-24">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-slate-600">Everything you need to know about investing with Veriquant</p>
          </div>
          <div className="space-y-6">
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
              <h3 className="text-xl font-bold text-slate-900 mb-3">What is a Merchant Cash Advance (MCA)?</h3>
              <p className="text-slate-700 leading-relaxed">
                A Merchant Cash Advance is a financing solution where businesses receive upfront capital in exchange for a percentage of future sales. MCAs are repaid through daily or weekly automatic deductions from the business's revenue, making them flexible and aligned with business performance. This alternative financing method has become increasingly popular for small businesses needing quick access to capital.
              </p>
            </div>
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
              <h3 className="text-xl font-bold text-slate-900 mb-3">How do I earn returns with Veriquant?</h3>
              <p className="text-slate-700 leading-relaxed">
                Through our partner platforms, you make direct investments into individual MCAs that have been vetted through rigorous AI-powered underwriting processes. Each MCA you invest in repays you directly through daily or weekly automatic payments as the business generates revenue. Your returns (typically 25-30% annually) come from the factor rate - the predetermined multiple that businesses pay back. You can reinvest these returns into new deals to compound your wealth, or withdraw funds as deals complete. You maintain complete control and ownership of each investment.
              </p>
            </div>
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
              <h3 className="text-xl font-bold text-slate-900 mb-3">What are the minimum investment requirements?</h3>
              <p className="text-slate-700 leading-relaxed">
                The minimum investment is $250,000 for direct MCA investments. Investment opportunities range from $250K up to $10M per allocation, providing institutional-grade opportunities for qualified investors. High-net-worth individuals, family offices, and institutional investors are our primary focus. Contact our investment team at 617.775.9876 to discuss eligibility requirements and available investment opportunities that match your portfolio size and investment strategy.
              </p>
            </div>
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
              <h3 className="text-xl font-bold text-slate-900 mb-3">How is my capital protected?</h3>
              <p className="text-slate-700 leading-relaxed">
                Our partner platforms employ multiple layers of protection: (1) Diversification across 15+ industries and hundreds of businesses, (2) AI-powered underwriting analyzing 150+ data points per deal, (3) Focus on businesses with average FICO scores of 720+, (4) Real-time portfolio monitoring and early warning systems, (5) Professional collections and recovery processes. The platforms we partner with maintain historical default rates of less than 6%, demonstrating the effectiveness of their risk management approach.
              </p>
            </div>
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
              <h3 className="text-xl font-bold text-slate-900 mb-3">Can I access my capital during the investment period?</h3>
              <p className="text-slate-700 leading-relaxed">
                Direct MCA investments have defined terms (typically 6-12 months) during which the business makes regular repayments. As payments come in daily or weekly, you receive your principal back plus returns over the deal's duration. Once a deal completes (usually within 6-12 months), you have full access to your capital and can choose to reinvest in new opportunities or withdraw. Many investors maintain a rolling portfolio where some deals are always completing, providing regular liquidity while others are just beginning.
              </p>
            </div>
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
              <h3 className="text-xl font-bold text-slate-900 mb-3">How do I track my investment performance?</h3>
              <p className="text-slate-700 leading-relaxed">
                Every investor receives 24/7 access to our comprehensive online portal aggregating real-time performance metrics from partner platforms, detailed transaction histories, industry exposure breakdowns, and personalized analytics. Monthly statements are automatically generated, and our team provides quarterly performance reviews. Mobile access ensures you can monitor your portfolio anywhere, anytime. Our portal includes advanced charting tools and benchmarking against traditional asset classes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section id="team" className="py-12 bg-slate-50 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Meet Our Team</h2>
            <p className="text-xl text-slate-600">Experienced professionals dedicated to your investment success</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Row 1 */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200 hover:shadow-xl hover:border-blue-300 transition-all">
              <img 
                src="https://customer-assets.emergentagent.com/job_3698f7b3-48f9-4e4c-b1bf-afbf06b65f39/artifacts/f1hnm420_image.png" 
                alt="Nermin Catic"
                className="w-56 h-56 object-cover rounded-lg mx-auto mb-4 border-4 border-slate-100 grayscale hover:grayscale-0 transition-all"
              />
              <h3 className="text-xl font-bold text-slate-900 mb-1 text-center">Nermin Catic</h3>
              <p className="text-blue-600 font-semibold text-sm mb-3 text-center">Acting Managing Director</p>
              <p className="text-slate-600 text-sm leading-relaxed mb-4 text-left">
                Nermin brings extensive experience in strategic leadership, financial operations, and organizational development. He drives VeriQuant's mission by aligning investment strategy, operational execution, and team performance.
              </p>
              <div className="flex flex-col gap-2 text-xs text-slate-600">
                <a href="mailto:nermin@veriquant.net" className="flex items-center justify-center hover:text-blue-600 transition-colors">
                  <span className="mr-2">✉️</span>
                  nermin@veriquant.net
                </a>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200 hover:shadow-xl hover:border-blue-300 transition-all">
              <img 
                src="https://customer-assets.emergentagent.com/job_3698f7b3-48f9-4e4c-b1bf-afbf06b65f39/artifacts/inm4mgi9_image.png" 
                alt="Stuart Simonsen"
                className="w-56 h-56 object-cover rounded-lg mx-auto mb-4 border-4 border-slate-100 grayscale hover:grayscale-0 transition-all"
              />
              <h3 className="text-xl font-bold text-slate-900 mb-1 text-center">Stuart Simonsen</h3>
              <p className="text-blue-600 font-semibold text-sm mb-3 text-center">Chief Investment Officer</p>
              <p className="text-slate-600 text-sm leading-relaxed mb-4 text-left">
                With nearly three decades of financial expertise, Stuart is the guiding force behind VeriQuant. He transforms financial challenges into opportunities for growth, simplifying the complex world of finance for investors and families.
              </p>
              <div className="flex flex-col gap-2 text-xs text-slate-600">
                <a href="mailto:stuart@veriquant.net" className="flex items-center justify-center hover:text-blue-600 transition-colors">
                  <span className="mr-2">✉️</span>
                  stuart@veriquant.net
                </a>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200 hover:shadow-xl hover:border-blue-300 transition-all">
              <img 
                src="https://customer-assets.emergentagent.com/job_3698f7b3-48f9-4e4c-b1bf-afbf06b65f39/artifacts/fd9ph55m_image.png" 
                alt="Mack McKaughan"
                className="w-56 h-56 object-cover rounded-lg mx-auto mb-4 border-4 border-slate-100 grayscale hover:grayscale-0 transition-all"
              />
              <h3 className="text-xl font-bold text-slate-900 mb-1 text-center">Mack McKaughan</h3>
              <p className="text-blue-600 font-semibold text-sm mb-3 text-center">Chief Operating Officer</p>
              <p className="text-slate-600 text-sm leading-relaxed mb-4 text-left">
                As COO at VeriQuant, Mack spearheads strategic initiatives that drive operational excellence. Collaborating with CEO and senior leadership, he oversees all day-to-day operations ensuring seamless functionality across departments.
              </p>
              <div className="flex flex-col gap-2 text-xs text-slate-600">
                <a href="mailto:mack@veriquant.net" className="flex items-center justify-center hover:text-blue-600 transition-colors">
                  <span className="mr-2">✉️</span>
                  mack@veriquant.net
                </a>
              </div>
            </div>

            {/* Row 2 */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200 hover:shadow-xl hover:border-blue-300 transition-all">
              <img 
                src="https://customer-assets.emergentagent.com/job_3698f7b3-48f9-4e4c-b1bf-afbf06b65f39/artifacts/eemfqtoy_image.png" 
                alt="Damir Karic"
                className="w-56 h-56 object-cover rounded-lg mx-auto mb-4 border-4 border-slate-100 grayscale hover:grayscale-0 transition-all"
              />
              <h3 className="text-xl font-bold text-slate-900 mb-1 text-center">Damir Karic</h3>
              <p className="text-blue-600 font-semibold text-sm mb-3 text-center">Head of International Growth</p>
              <p className="text-slate-600 text-sm leading-relaxed mb-4 text-left">
                Damir focuses on expanding VeriQuant's reach across new global markets. With expertise in cross-border business development and navigating regulatory landscapes, he introduces VeriQuant's investment model to international investors.
              </p>
              <div className="flex flex-col gap-2 text-xs text-slate-600">
                <a href="mailto:damir@veriquant.net" className="flex items-center justify-center hover:text-blue-600 transition-colors">
                  <span className="mr-2">✉️</span>
                  damir@veriquant.net
                </a>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200 hover:shadow-xl hover:border-blue-300 transition-all">
              <img 
                src="https://customer-assets.emergentagent.com/job_3698f7b3-48f9-4e4c-b1bf-afbf06b65f39/artifacts/s88ywst2_image.png" 
                alt="Gina Martin"
                className="w-56 h-56 object-cover rounded-lg mx-auto mb-4 border-4 border-slate-100 grayscale hover:grayscale-0 transition-all"
              />
              <h3 className="text-xl font-bold text-slate-900 mb-1 text-center">Gina Martin</h3>
              <p className="text-blue-600 font-semibold text-sm mb-3 text-center">Head of Administration</p>
              <p className="text-slate-600 text-sm leading-relaxed mb-4 text-left">
                With over 15 years of experience, Gina leads the administrative division at VeriQuant, ensuring efficient coordination of internal processes. Her detail-oriented approach fosters a structured, high-performance environment supporting long-term success.
              </p>
              <div className="flex flex-col gap-2 text-xs text-slate-600">
                <a href="mailto:gina@veriquant.net" className="flex items-center justify-center hover:text-blue-600 transition-colors">
                  <span className="mr-2">✉️</span>
                  gina@veriquant.net
                </a>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200 hover:shadow-xl hover:border-blue-300 transition-all">
              <img 
                src="https://customer-assets.emergentagent.com/job_3698f7b3-48f9-4e4c-b1bf-afbf06b65f39/artifacts/j32jnf57_image.png" 
                alt="Alyssa Simonsen"
                className="w-56 h-56 object-cover rounded-lg mx-auto mb-4 border-4 border-slate-100 grayscale hover:grayscale-0 transition-all"
              />
              <h3 className="text-xl font-bold text-slate-900 mb-1 text-center">Alyssa Simonsen</h3>
              <p className="text-blue-600 font-semibold text-sm mb-3 text-center">Portfolio Manager</p>
              <p className="text-slate-600 text-sm leading-relaxed mb-4 text-left">
                As Portfolio Manager, Alyssa delivers superior investment solutions and optimizes portfolio performance to achieve clients' financial objectives. She leads development of investment strategies tailored to client needs and risk profiles.
              </p>
              <div className="flex flex-col gap-2 text-xs text-slate-600">
                <a href="mailto:alyssa@veriquant.net" className="flex items-center justify-center hover:text-blue-600 transition-colors">
                  <span className="mr-2">✉️</span>
                  alyssa@veriquant.net
                </a>
              </div>
            </div>

            {/* Row 3 */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200 hover:shadow-xl hover:border-blue-300 transition-all">
              <img 
                src="https://customer-assets.emergentagent.com/job_3698f7b3-48f9-4e4c-b1bf-afbf06b65f39/artifacts/9s91h4lk_image.png" 
                alt="Ingrid Cajic"
                className="w-56 h-56 object-cover rounded-lg mx-auto mb-4 border-4 border-slate-100 grayscale hover:grayscale-0 transition-all"
              />
              <h3 className="text-xl font-bold text-slate-900 mb-1 text-center">Ingrid Cajic</h3>
              <p className="text-blue-600 font-semibold text-sm mb-3 text-center">Head of Technology</p>
              <p className="text-slate-600 text-sm leading-relaxed mb-4 text-left">
                As Head of Technology, Ingrid leads technology initiatives and drives innovation to propel VeriQuant forward. She collaborates with the team to explore new technologies, identify improvements, and implement strategic solutions.
              </p>
              <div className="flex flex-col gap-2 text-xs text-slate-600">
                <a href="mailto:ingrid@veriquant.net" className="flex items-center justify-center hover:text-blue-600 transition-colors">
                  <span className="mr-2">✉️</span>
                  ingrid@veriquant.net
                </a>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200 hover:shadow-xl hover:border-blue-300 transition-all">
              <img 
                src="https://customer-assets.emergentagent.com/job_3698f7b3-48f9-4e4c-b1bf-afbf06b65f39/artifacts/27552kep_image.png" 
                alt="Feda Cajic"
                className="w-56 h-56 object-cover rounded-lg mx-auto mb-4 border-4 border-slate-100 grayscale hover:grayscale-0 transition-all"
              />
              <h3 className="text-xl font-bold text-slate-900 mb-1 text-center">Feda Cajic</h3>
              <p className="text-blue-600 font-semibold text-sm mb-3 text-center">Social Media Coordinator</p>
              <p className="text-slate-600 text-sm leading-relaxed mb-4 text-left">
                As Social Media Coordinator, Feda leverages the power of social media to enhance brand visibility and engage with audiences. With keen understanding of trends, she develops comprehensive social media strategies aligned with company goals.
              </p>
              <div className="flex flex-col gap-2 text-xs text-slate-600">
                <a href="mailto:feda@veriquant.net" className="flex items-center justify-center hover:text-blue-600 transition-colors">
                  <span className="mr-2">✉️</span>
                  feda@veriquant.net
                </a>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200 hover:shadow-xl transition-shadow">
              <img 
                src="https://customer-assets.emergentagent.com/job_3698f7b3-48f9-4e4c-b1bf-afbf06b65f39/artifacts/6n6j9nsl_image.png" 
                alt="Rolando Laderas"
                className="w-56 h-56 object-cover rounded-lg mx-auto mb-4 border-4 border-cyan-100 grayscale hover:grayscale-0 transition-all"
              />
              <h3 className="text-xl font-bold text-slate-900 mb-1 text-center">Rolando Laderas</h3>
              <p className="text-cyan-600 font-semibold text-sm mb-3 text-center">Administrative Executive</p>
              <p className="text-slate-600 text-sm leading-relaxed mb-4 text-left">
                As Administrative Executive, Rolando ensures smooth and efficient operation of the executive office. With focus on organization and attention to detail, he facilitates day-to-day activities enabling executives to focus on strategic initiatives.
              </p>
              <div className="flex flex-col gap-2 text-xs text-slate-600">
                <a href="mailto:rolando@veriquant.net" className="flex items-center justify-center hover:text-cyan-600 transition-colors">
                  <span className="mr-2">✉️</span>
                  rolando@veriquant.net
                </a>
              </div>
            </div>
          </div>
          
          <div className="mt-12 bg-white rounded-2xl p-8 border border-slate-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-4xl font-bold text-blue-600 mb-2">100+</div>
                <div className="text-slate-600">Years Combined Experience</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-green-600 mb-2">$2B+</div>
                <div className="text-slate-600">Capital Deployed</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-violet-600 mb-2">45,000+</div>
                <div className="text-slate-600">Investments Funded</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-12 bg-white scroll-mt-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Get In Touch</h2>
            <p className="text-xl text-slate-600">Ready to start investing? Our team is here to help</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div className="space-y-8">
              <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 border border-blue-200">
                <div className="flex items-start">
                  <div className="bg-blue-600 text-white p-4 rounded-xl mr-6">
                    <Phone className="w-8 h-8" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Call Us</h3>
                    <p className="text-slate-600 mb-3">Speak directly with our investment team</p>
                    <a href="tel:6177759876" className="text-2xl font-bold text-blue-600 hover:text-blue-700">617.775.9876</a>
                    <p className="text-sm text-slate-500 mt-2">Monday - Friday: 9:00 AM - 6:00 PM MST</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-green-50 to-white rounded-2xl p-8 border border-green-200">
                <div className="flex items-start">
                  <div className="bg-green-600 text-white p-4 rounded-xl mr-6">
                    <Users className="w-8 h-8" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Schedule a Consultation</h3>
                    <p className="text-slate-600 mb-4">Book a personalized meeting with a member of our team</p>
                    <Button 
                      onClick={() => window.location.href = 'tel:6177759876'}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      Book Appointment
                    </Button>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-violet-50 to-white rounded-2xl p-8 border border-violet-200">
                <div className="flex items-start">
                  <div className="bg-violet-600 text-white p-4 rounded-xl mr-6">
                    <Target className="w-8 h-8" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Visit Our Office</h3>
                    <p className="text-slate-600 mb-2">Veriquant LLC</p>
                    <p className="text-slate-700 font-medium">
                      6310 S Fiddlers Green Circle #300 East<br />
                      Greenwood Village, CO 80111
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Contact Form */}
            <div className="bg-slate-900 text-white rounded-2xl p-8 shadow-2xl">
              <h3 className="text-2xl font-bold mb-6">Quick Contact Form</h3>
              <form className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Full Name</label>
                  <input 
                    type="text" 
                    className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Email Address</label>
                  <input 
                    type="email" 
                    className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Phone Number</label>
                  <input 
                    type="tel" 
                    className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Investment Amount</label>
                  <select className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                    <option>$250,000 - $500,000</option>
                    <option>$500,000 - $1,000,000</option>
                    <option>$1,000,000 - $2,500,000</option>
                    <option>$2,500,000 - $5,000,000</option>
                    <option>$5,000,000 - $10,000,000</option>
                    <option>$10,000,000+</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Are you an Accredited Investor?</label>
                  <select className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                    <option value="">Select an option</option>
                    <option value="yes">Yes, I am an Accredited Investor</option>
                    <option value="no">No, I am not an Accredited Investor</option>
                    <option value="unsure">Not sure / Need more information</option>
                  </select>
                  <p className="text-xs text-slate-400 mt-2">
                    Accredited investors have net worth over $1M (excluding primary residence) or annual income over $200K ($300K joint).
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Message</label>
                  <textarea 
                    rows="3"
                    className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Tell us about your investment goals..."
                  ></textarea>
                </div>
                <Button 
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 text-lg"
                >
                  Send Message
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section className="py-12 bg-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-4">Assistance Every Step of the Way</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Veriquant provides hands-on support to all users on the platform. Our knowledgeable team of representatives are available to assist with any questions or concerns you may have.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button
              size="lg"
              variant="outline"
              onClick={() => window.location.href = 'tel:6177759876'}
              className="bg-white text-blue-600 hover:bg-slate-50 px-8 py-6 text-lg border-none"
            >
              <Phone className="w-5 h-5 mr-2" />
              Book a Time with The Investment Team
            </Button>
            <a href="tel:6177759876" className="text-xl font-semibold hover:underline">
              617.775.9876
            </a>
          </div>
        </div>
      </section>

      {/* Legal Disclaimer Section */}
      <section className="py-16 bg-slate-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-red-200">
            <div className="flex items-start mb-4">
              <Shield className="w-8 h-8 text-red-600 mr-4 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Important Investment Disclaimers</h3>
                
                <div className="space-y-4 text-slate-700">
                  <div className="bg-red-50 border-l-4 border-red-600 p-4 rounded">
                    <h4 className="font-bold text-red-900 mb-2">⚠️ Investment Risk Warning</h4>
                    <p className="text-sm leading-relaxed">
                      <strong>ALL INVESTMENTS INVOLVE RISK.</strong> Investing in Merchant Cash Advances (MCAs) and alternative investments carries substantial risk, including the potential loss of your entire principal investment. Past performance is not indicative of future results. You should carefully consider your financial situation, investment objectives, and risk tolerance before investing. <strong>INVEST AT YOUR OWN RISK.</strong>
                    </p>
                  </div>

                  <div className="bg-yellow-50 border-l-4 border-yellow-600 p-4 rounded">
                    <h4 className="font-bold text-yellow-900 mb-2">📊 Data & Metrics Disclosure</h4>
                    <p className="text-sm leading-relaxed">
                      Some information, data, metrics, and performance statistics displayed on this website represent <strong>sample data, illustrative examples, and aggregated partner platform metrics</strong>. These figures are provided for informational purposes only and may not reflect actual performance of specific investments or VeriQuant's direct portfolio. Individual investment results may vary significantly.
                    </p>
                  </div>

                  <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded">
                    <h4 className="font-bold text-blue-900 mb-2">🔒 Accredited Investors Only</h4>
                    <p className="text-sm leading-relaxed">
                      VeriQuant's investment opportunities are available exclusively to <strong>accredited investors</strong> as defined by SEC Regulation D. Minimum investment of $250,000 required. Investments are illiquid and subject to holding periods. Early withdrawal may not be available.
                    </p>
                  </div>

                  <div className="bg-slate-50 border-l-4 border-slate-600 p-4 rounded">
                    <h4 className="font-bold text-slate-900 mb-2">⚖️ No Guarantee & Liability Limitation</h4>
                    <p className="text-sm leading-relaxed">
                      VeriQuant makes <strong>no guarantees</strong> regarding investment returns, capital preservation, or portfolio performance. We are not responsible for investment losses, market fluctuations, borrower defaults, or any financial outcomes resulting from your investment decisions. Returns ranging from 25-30% annually are <strong>targets only</strong> and not guaranteed. Actual returns may be higher or lower.
                    </p>
                  </div>

                  <div className="bg-violet-50 border-l-4 border-violet-600 p-4 rounded">
                    <h4 className="font-bold text-violet-900 mb-2">📋 Seek Professional Advice</h4>
                    <p className="text-sm leading-relaxed">
                      Before making any investment decision, you should consult with qualified financial, legal, and tax advisors. This website does not constitute investment advice, and VeriQuant is not a registered investment advisor. All investment decisions are your sole responsibility.
                    </p>
                  </div>

                  <p className="text-xs text-slate-500 mt-6 italic">
                    By accessing this website and/or investing with VeriQuant, you acknowledge that you have read, understood, and agree to these disclaimers and that you are making investment decisions independently and at your own risk.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_data-investor/artifacts/g2epkoel_image.png"
                alt="Veriquant"
                className="h-16 w-auto mb-4 brightness-0 invert"
              />
              <p className="text-slate-400">
                Alternative investments in small business finance.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-lg mb-4">Quick Links</h4>
              <ul className="space-y-2 text-slate-400">
                <li><button onClick={() => navigate('/login')} className="hover:text-white">Investor Portal</button></li>
                <li><button onClick={() => navigate('/admin/login')} className="hover:text-white">Admin Portal</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-lg mb-4">Contact</h4>
              <ul className="space-y-2 text-slate-400">
                <li><a href="tel:6177759876" className="hover:text-white">617.775.9876</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8">
            <div className="text-center text-slate-400 mb-4">
              <p className="text-xs leading-relaxed max-w-5xl mx-auto mb-2">
                <strong className="text-slate-300">INVESTMENT RISK DISCLOSURE:</strong> All investments carry risk including loss of principal. Past performance does not guarantee future results. VeriQuant is not responsible for investment losses. Some data represents sample metrics and partner platform aggregates. <strong>Invest at your own risk.</strong> Accredited investors only. Minimum $250K. Consult advisors before investing.
              </p>
            </div>
            <div className="text-center text-slate-400">
              {/* Social Media Links */}
              <div className="flex justify-center gap-6 mb-6">
                <a 
                  href="https://www.instagram.com/veriquant/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-slate-400 hover:text-white transition-colors"
                  aria-label="Instagram"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
                <a 
                  href="https://www.facebook.com/profile.php?id=61578391771762" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-slate-400 hover:text-white transition-colors"
                  aria-label="Facebook"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a 
                  href="https://www.linkedin.com/company/veriquant-asset-management/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-slate-400 hover:text-white transition-colors"
                  aria-label="LinkedIn"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
              </div>
              <p>&copy; 2025 Veriquant. All rights reserved.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
